import React, { useEffect, useRef, useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import Fuse from "fuse.js";

import './SearchComponent.scss';
import {SearchResult} from "./models";

interface QueryData {
    allSitePage: {
        nodes: Array<{
            id: string;
            path: string;
            context: {
                title: string;
            };
        }>;
    };
}

const SearchComponent: React.FC = () => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

    const data: QueryData = useStaticQuery(graphql`
    query {
      allSitePage {
        nodes {
          id
          path
          context {
            title
          }
        }
      }
    }
  `);

    const SEARCH_CONFIG = {
        keys: ["id", "path", "title"],
        threshold: 0.3,
        ignoreLocation: true,
        includeScore: true,
        useExtendedSearch: true,
    };

    const searchItems = data.allSitePage.nodes.map(({id, path, context: {title}}) => ({
        id,
        path,
        title,
    }));

    const fuse = new Fuse(searchItems, SEARCH_CONFIG);

    const results: SearchResult[] = query.length >= 2 ? fuse.search(query) : [];
    useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('click', handleClick);

    return () => document.removeEventListener('click', handleClick);
  }, []);

    const SearchResultItem = ({item}) => (<a
        key={item.id}
        href={item.path}
        className="ais-Hits-item"
        onClick={() => setOpen(false)}
    >
        <div className="font-medium text-gray-800">{item.title}</div>
    </a>)

    return (
    <div ref={wrapperRef} className="ais-SearchBox relative">
      <input
        type="text"
        placeholder="Search..."
        value={query}
        onChange={(e) => {
          const val = e.target.value;
          setQuery(val);
          setOpen(val.length >= 2);
        }}
        onFocus={() => query.length >= 2 && setOpen(true)}
        className="ais-SearchBox-input"
        aria-label="Search"
      />

      <svg
        className="ais-SearchBox-icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 40 40"
        fill="currentColor"
      >
        <path d="M26.804 29.01c-2.832 2.34-6.465 3.746-10.426 3.746C7.333 32.756 0 25.424 0 16.378 0 7.333 7.333 0 16.378 0c9.046 0 16.378 7.333 16.378 16.378 0 3.96-1.406 7.594-3.746 10.426l10.534 10.534c.607.607.61 1.59-.004 2.202-.61.61-1.597.61-2.202.004L26.804 29.01zm-10.426.627c7.323 0 13.26-5.936 13.26-13.26 0-7.32-5.937-13.257-13.26-13.257C9.056 3.12 3.12 9.056 3.12 16.378c0 7.323 5.936 13.26 13.258 13.26z" />
      </svg>

      {open && (
        <div className="ais-Hits absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg">
          <div className="ais-Hits-list">
            {results.length > 0 ? (
              results.filter(r => r.item.title).map((result: SearchResult) => (
                <SearchResultItem item={result.item} />
              ))
            ) : (
              <div className="p-3 text-sm text-gray-500">No results found</div>
            )}
          </div>
          <button
            type="reset"
            className="ais-SearchBox-reset"
            onClick={() => {
              setQuery('');
              setOpen(false);
            }}
            aria-label="Clear search"
          >
            <svg
              className="ais-SearchBox-resetIcon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              width="10"
              height="10"
            >
              <path d="M8.114 10L.944 2.83 0 1.885 1.886 0l.943.943L10 8.113l7.17-7.17.944-.943L20 1.886l-.943.943-7.17 7.17 7.17 7.17.943.944L18.114 20l-.943-.943-7.17-7.17-7.17 7.17-.944.943L0 18.114l.943-.943L8.113 10z" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default SearchComponent;
